/**
 * Module configuration for the application.
 * Consumes modules_entities.config.json and provides typed module/entity access.
 */

import React from 'react';
import modulesEntitiesConfig from '../../shared-config/modules_entities.config.json';
import { getIcon } from './iconMap';

// Types derived from the JSON config
export interface NavItem {
  labelKey: string;
  descriptionKey?: string;
  path: string;
  icon: React.ReactNode;
  /** If set, renders a section header label before this item. Value is an i18n key. */
  section?: string;
  /**
   * Language-independent search aliases for this page in the module picker.
   * Folded into the picker's fuzzy search so the page is findable by an
   * alternative name (e.g. ['estimates'] for a Quotes page). Never displayed,
   * never translated — kept literal so they match regardless of UI language.
   */
  searchSynonyms?: string[];
  /** If true, this nav item is only shown when the user has Google connected */
  requiresGoogleConnection?: boolean;
  /** If true, this nav item is only shown when the user has Microsoft connected */
  requiresMicrosoftConnection?: boolean;
  /** If true, show this nav item even when it matches the module's defaultPath */
  alwaysShowInNav?: boolean;
  /** If true, this nav item is only shown to admin users */
  requiresAdmin?: boolean;
  /** If true, this nav item is only shown to sysadmin users (sysadmin tenant) */
  requiresSysadmin?: boolean;
  /**
   * If true, this nav item is only shown when the tenant operates as a freight
   * forwarder (persona flag `sells_freight_services` on — Forwarder TMS). The
   * sidebar reads the persona via `usePersona` and hides the item when off.
   */
  requiresForwarderMode?: boolean;
  /**
   * If set, this nav item is a cross-module link to another module (canonical owner of the data).
   * Value is the source module ID (e.g., "master_data"). The sidebar shows an indicator + tooltip
   * to signal that clicking will navigate out of the current module.
   * Pattern: only for master-data / shared entities that live in another module.
   */
  crossModule?: string;
}

export interface ColorScheme {
  primary: string;
  light?: string;
  dark?: string;
  contrastText?: string;
}

export interface EntityConfig {
  table: string | null;
  description: string;
  displayNameKey: string;
  pluralKey: string;
  creatable?: boolean;
  icon?: React.ReactNode;
  /** Parent entity key within the same module (for navigation hierarchy) */
  parent?: string;
  /** If true, this entity cannot exist without its parent (required FK). Default false. */
  parentRequired?: boolean;
}

export interface ModuleSettingsConfig {
  /** If true, module has user-level settings accessible from sidebar */
  hasUserSettings?: boolean;
  /** If true, module has tenant-level settings (requires admin role) */
  hasTenantSettings?: boolean;
  /** If true, module has sysadmin-level settings (requires sysadmin role) */
  hasSysadminSettings?: boolean;
  /** Full-page route for user settings (navigates instead of opening dialog) */
  userSettingsPath?: string;
  /** Route for tenant-level admin settings (shown to admins in module sidebar) */
  tenantSettingsPath?: string;
}

export type ModuleStatus = 'stable' | 'beta' | 'alpha' | 'hidden';
export type ModuleCategory = 'master_data' | 'internal' | 'technical' | 'admin';
/**
 * Domain grouping for the home launcher — buckets "Other modules" under kicker
 * section headers (see docs/module-list.md). Orthogonal to {@link ModuleCategory}
 * (which drives the small type badge). Modules with no group fall into a trailing
 * "More" bucket so nothing is ever dropped.
 */
export type ModuleGroup =
  | 'master_data'
  | 'core_operations'
  | 'supply_chain'
  | 'communication'
  | 'ai'
  | 'product_tools'
  | 'admin';

export interface ModuleConfig {
  id: string;
  nameKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  /** Raw icon name string from config (used for canvas favicon rendering) */
  iconName: string;
  color: string;
  basePaths: string[];
  defaultPath: string;
  navItems: NavItem[];
  /**
   * Language-independent search aliases for this module in the module picker.
   * Folded into the picker's fuzzy search so the module is findable by an
   * alternative name (e.g. ['CRM', 'deals'] for pipeline). Never displayed,
   * never translated — kept literal so they match regardless of UI language.
   */
  searchSynonyms?: string[];
  entities: Record<string, EntityConfig>;
  /** Only show to users with admin role */
  requiresAdmin?: boolean;
  /** Only show to users with sysadmin role */
  requiresSysadmin?: boolean;
  /** Only show in development/debug mode */
  devOnly?: boolean;
  /** Internal module (no UI in module picker) */
  internal?: boolean;
  /** Order in which module appears in home page and module picker (lower = first) */
  displayOrder: number;
  /** If true, module appears directly in the module picker grid */
  showInModulePicker: boolean;
  /** Module maturity status: stable (default), beta, or alpha */
  status: ModuleStatus;
  /** Module category (e.g., master_data for shared reference data modules) */
  category?: ModuleCategory;
  /** Domain grouping for the home launcher (buckets "Other modules" under kicker headers) */
  group?: ModuleGroup;
  /** Settings configuration for sidebar */
  settings?: ModuleSettingsConfig;
  /** If true, the secondary nav panel is completely hidden for this module */
  hideSecondaryNav?: boolean;
  /** If true, module is pinned at the bottom of the module picker (utility modules like Files, Mail) */
  pinned?: boolean;
  /** Access model from modules_entities.config.json (e.g. 'rebac', 'module_onoff') */
  accessModel?: string;
}

// Raw entity config type from JSON (before icon transformation)
interface RawEntityConfig {
  table?: string | null;
  description: string;
  displayNameKey: string;
  pluralKey: string;
  creatable?: boolean;
  icon?: string;
  parent?: string;
  parentRequired?: boolean;
}

// Raw config type from JSON
interface RawModuleConfig {
  id: string;
  displayNameKey: string;
  descriptionKey: string;
  defaultColorScheme?: { primary: string };
  icon?: string;
  basePaths?: string[];
  defaultPath?: string;
  navItems?: Array<{ labelKey: string; descriptionKey?: string; path: string; icon: string; section?: string; searchSynonyms?: string[]; requiresGoogleConnection?: boolean; requiresMicrosoftConnection?: boolean; alwaysShowInNav?: boolean; requiresAdmin?: boolean; requiresSysadmin?: boolean; requiresForwarderMode?: boolean; crossModule?: string }>;
  searchSynonyms?: string[];
  entities: Record<string, RawEntityConfig>;
  internal?: boolean;
  toggleable?: boolean;
  alwaysAvailable?: boolean;
  requiresAdmin?: boolean;
  requiresSysadmin?: boolean;
  devOnly?: boolean;
  displayOrder?: number;
  showInModulePicker?: boolean;
  /**
   * If false, the module is NOT enabled by default for a new tenant
   * (opt-in only, e.g. software_factory). Consumed backend-side at tenant
   * creation; declared here so the JSON shape stays typed.
   */
  defaultEnabled?: boolean;
  pinned?: boolean;
  status?: ModuleStatus;
  category?: ModuleCategory;
  group?: ModuleGroup;
  settings?: ModuleSettingsConfig;
  hideSecondaryNav?: boolean;
  accessModel?: string;
}

// Transform raw entity config to typed EntityConfig
function transformEntity(rawEntity: RawEntityConfig): EntityConfig {
  return {
    table: rawEntity.table ?? null,
    description: rawEntity.description,
    displayNameKey: rawEntity.displayNameKey,
    pluralKey: rawEntity.pluralKey,
    creatable: rawEntity.creatable,
    icon: rawEntity.icon ? getIcon(rawEntity.icon) : undefined,
    parent: rawEntity.parent,
    parentRequired: rawEntity.parentRequired,
  };
}

// Transform raw JSON config to typed ModuleConfig
function transformModule(rawModule: RawModuleConfig): ModuleConfig {
  const transformedEntities: Record<string, EntityConfig> = {};
  for (const [key, entity] of Object.entries(rawModule.entities)) {
    transformedEntities[key] = transformEntity(entity);
  }

  return {
    id: rawModule.id,
    nameKey: rawModule.displayNameKey,
    descriptionKey: rawModule.descriptionKey,
    icon: getIcon(rawModule.icon) ?? <></>,
    iconName: rawModule.icon ?? '',
    color: rawModule.defaultColorScheme?.primary ?? '#616161',
    basePaths: rawModule.basePaths ?? [],
    defaultPath: rawModule.defaultPath ?? '/',
    navItems: (rawModule.navItems ?? []).map(item => ({
      labelKey: item.labelKey,
      descriptionKey: item.descriptionKey,
      path: item.path,
      icon: getIcon(item.icon) ?? <></>,
      section: item.section,
      searchSynonyms: item.searchSynonyms,
      requiresGoogleConnection: item.requiresGoogleConnection,
      requiresMicrosoftConnection: item.requiresMicrosoftConnection,
      alwaysShowInNav: item.alwaysShowInNav,
      requiresAdmin: item.requiresAdmin,
      requiresSysadmin: item.requiresSysadmin,
      requiresForwarderMode: item.requiresForwarderMode,
      crossModule: item.crossModule,
    })),
    searchSynonyms: rawModule.searchSynonyms,
    entities: transformedEntities,
    requiresAdmin: rawModule.requiresAdmin,
    requiresSysadmin: rawModule.requiresSysadmin,
    devOnly: rawModule.devOnly,
    internal: rawModule.internal,
    displayOrder: rawModule.displayOrder ?? 500,
    showInModulePicker: rawModule.showInModulePicker ?? false,
    status: rawModule.status ?? 'stable',
    category: rawModule.category,
    group: rawModule.group,
    settings: rawModule.settings,
    hideSecondaryNav: rawModule.hideSecondaryNav,
    pinned: rawModule.pinned,
    accessModel: rawModule.accessModel,
  };
}

// Build MODULES array from config, filtering out internal modules for UI purposes
const rawModules = modulesEntitiesConfig.modules as Record<string, RawModuleConfig>;

// Sort by displayOrder (lower = first)
const sortByDisplayOrder = (a: ModuleConfig, b: ModuleConfig) => a.displayOrder - b.displayOrder;

export const MODULES: ModuleConfig[] = Object.values(rawModules)
  .filter(m => !m.internal)
  .map(transformModule)
  .sort(sortByDisplayOrder);

// All modules including internal ones (for entity lookups)
export const ALL_MODULES: ModuleConfig[] = Object.values(rawModules)
  .map(transformModule)
  .sort(sortByDisplayOrder);

/** Modules that should appear directly in the module picker grid (excluding pinned) */
export const MODULE_PICKER_MODULES: ModuleConfig[] = MODULES.filter(m => m.showInModulePicker && !m.pinned);

/** Utility modules pinned at the bottom of the module picker */
export const PINNED_MODULES: ModuleConfig[] = MODULES.filter(m => m.showInModulePicker && m.pinned);

/**
 * Order in which domain groups render in the home launcher. A sentinel `'more'`
 * bucket collects any module whose `group` is unset so nothing is ever dropped.
 * Labels come from i18n (`modules.group.{id}`) — the id order is fixed here.
 */
export const MODULE_GROUP_ORDER = [
  'master_data',
  'core_operations',
  'supply_chain',
  'communication',
  'ai',
  'product_tools',
  'admin',
  'more',
] as const;
export type ModuleGroupKey = (typeof MODULE_GROUP_ORDER)[number];

/** Bucket a module into its launcher group key (falls back to the `more` sentinel). */
export function moduleGroupKey(module: ModuleConfig): ModuleGroupKey {
  return module.group ?? 'more';
}

/**
 * Split modules into their domain groups, preserving each group's incoming
 * order and dropping empty groups. Used by the home launcher to render the
 * "Other modules" section under kicker headers.
 */
export function groupModulesByDomain(
  modules: ModuleConfig[]
): Array<{ key: ModuleGroupKey; modules: ModuleConfig[] }> {
  const byKey = new Map<ModuleGroupKey, ModuleConfig[]>();
  for (const m of modules) {
    const key = moduleGroupKey(m);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(m);
    else byKey.set(key, [m]);
  }
  return MODULE_GROUP_ORDER
    .filter((key) => byKey.has(key))
    .map((key) => ({ key, modules: byKey.get(key)! }));
}

/**
 * Module IDs that can be enabled/disabled via settings (not role-based).
 * Hidden modules (status:hidden) are excluded — they must not appear in any
 * module picker (Settings, onboarding, home). They remain valid as a tenant's
 * enabled_modules (server-side validation is broader) so existing tenants and
 * sysadmins aren't broken; they're simply not offered in the picker.
 */
export const TOGGLEABLE_MODULE_IDS = Object.entries(rawModules)
  .filter(([, m]) => m.toggleable && m.status !== 'hidden')
  .map(([, m]) => m.id) as string[];
export type ToggleableModuleId = (typeof TOGGLEABLE_MODULE_IDS)[number];

/** Module IDs that are always available (cannot be disabled) */
export const ALWAYS_AVAILABLE_MODULE_IDS = Object.entries(rawModules)
  .filter(([, m]) => m.alwaysAvailable)
  .map(([, m]) => m.id) as string[];

/** Module IDs that depend on user role, not settings */
export const ROLE_BASED_MODULE_IDS = Object.entries(rawModules)
  .filter(([, m]) => m.requiresAdmin || m.requiresSysadmin || m.devOnly)
  .map(([, m]) => m.id) as string[];

// Module color definitions (extracted from config for quick access)
export const MODULE_COLORS: Record<string, string> = Object.fromEntries(
  Object.values(rawModules)
    .filter(m => m.defaultColorScheme?.primary)
    .map(m => [m.id, m.defaultColorScheme!.primary])
);

/**
 * Mapping from module config keys (Python folder names like "ai_agents", "website_hosting")
 * to their URL base paths (like "ai-agents", "websites"). Used by getEntityUrl() to resolve
 * entity refs with multi-word module prefixes.
 * Sorted by key length descending so longer prefixes match first.
 */
export const MODULE_KEY_TO_BASE_PATH: Array<[string, string]> = Object.entries(rawModules)
  .map(([key, m]) => [key, (m.basePaths?.[0] ?? m.defaultPath ?? `/${key}`).replace(/^\//, '')] as [string, string])
  .sort((a, b) => b[0].length - a[0].length);

/**
 * Find the module that matches a given path
 */
export function getModuleByPath(pathname: string): ModuleConfig {
  // Exact match for home
  if (pathname === '/') {
    return MODULES.find(m => m.id === 'home')!;
  }

  // Find module by matching basePaths
  for (const module of MODULES) {
    if (module.id === 'home') continue; // Skip home for prefix matching
    for (const basePath of module.basePaths) {
      if (pathname.startsWith(basePath)) {
        return module;
      }
    }
  }

  // Default to home if no match
  return MODULES.find(m => m.id === 'home')!;
}

/**
 * Resolve where an invalid / unmatched route should redirect to.
 *
 * If the path's leading segments match a known module's `basePaths`, returns
 * that module's `defaultPath` (its root) so the user lands somewhere relevant.
 * Otherwise returns `'/'` (the app root). Unlike {@link getModuleByPath}, this
 * never silently falls back to the home module — a non-match returns `'/'`
 * explicitly, which is what the global 404 handler wants.
 */
export function getRedirectPathForUnmatched(pathname: string): string {
  if (pathname === '/' || pathname === '') {
    return '/';
  }
  for (const module of MODULES) {
    if (module.id === 'home') continue;
    for (const basePath of module.basePaths) {
      // Match the full base segment only (avoid `/sale` matching `/sales`).
      if (pathname === basePath || pathname.startsWith(`${basePath}/`)) {
        return module.defaultPath || basePath;
      }
    }
  }
  return '/';
}

/**
 * Get a module by its ID
 */
export function getModuleById(id: string): ModuleConfig | undefined {
  return ALL_MODULES.find(m => m.id === id);
}

/**
 * Module IDs whose bare root path (`basePaths[0]`) intentionally renders a page
 * directly in the router, even though their `defaultPath` is a deeper subpath.
 * These roots must NOT get a module-root redirect (doing so would double-declare
 * the path, or shadow a bare-root page that is distinct from the default page —
 * e.g. `/ai-agents` shows the agents overview, `/brokerage` the brokerage
 * dashboard, `/shipments` the shipments overview). Kept as an explicit allowlist
 * so a newly-added module with a subpath default can't silently start 404ing on
 * its bare root without a deliberate choice here (enforced by the route test).
 */
export const DIRECT_RENDER_ROOT_MODULE_IDS: ReadonlySet<string> = new Set([
  'tasks', // /tasks -> index Navigate to dashboard
  'contracts', // /contracts -> index renders dashboard
  'ai', // /ai-agents -> agents overview page (distinct from dashboard)
  'support', // /support -> dashboard
  'shipments', // /shipments -> shipments overview (distinct from dashboard)
  'inventory', // /inventory -> dashboard
  'procurement', // /procurement -> dashboard
  'sales', // /sales -> dashboard
  'brokerage', // /brokerage -> dashboard (distinct from offer-sheets default)
  'billing', // /billing -> accounts list (the default page)
  'chatbot', // /chatbot -> dashboard
]);

/**
 * Does a module's default page live at a deeper subpath than its bare root?
 * (`/pipeline` root vs `/pipeline/dashboard` default). Internal modules with no
 * base path, and modules whose default already IS the root, return false.
 */
export function hasSubpathDefault(module: ModuleConfig): boolean {
  const root = module.basePaths[0];
  if (!root) return false;
  return module.defaultPath !== root && module.defaultPath.startsWith(`${root}/`);
}

/**
 * Modules whose bare root path has no route of its own and would otherwise fall
 * through to the smart 404. Each bare root should redirect straight to the
 * module's configured `defaultPath` so first-party navigation never flashes the
 * "Page not found → redirecting…" page. Derived from config (targets stay in
 * sync with `defaultPath`) as every subpath-default module NOT in
 * {@link DIRECT_RENDER_ROOT_MODULE_IDS}. Consumed by `App.tsx` route wiring.
 */
export const MODULE_ROOT_REDIRECTS: ReadonlyArray<{ from: string; to: string }> =
  ALL_MODULES.filter(
    m => hasSubpathDefault(m) && !DIRECT_RENDER_ROOT_MODULE_IDS.has(m.id)
  ).map(m => ({ from: m.basePaths[0], to: m.defaultPath }));

/**
 * Get modules filtered by user permissions (role-based only, not settings-based)
 */
export function getAccessibleModules(
  isAdmin: boolean,
  isSysadmin: boolean,
  showDevModules: boolean = false
): ModuleConfig[] {
  return MODULES.filter(module => {
    if (module.internal) return false;
    if (module.status === 'hidden') return false;
    if (module.requiresSysadmin && !isSysadmin) return false;
    if (module.requiresAdmin && !isAdmin && !isSysadmin) return false;
    if (module.devOnly && !showDevModules) return false;
    return true;
  });
}

/**
 * Compute effective enabled modules for a user based on platform, tenant, and user settings.
 * Returns modules that are enabled at all levels (intersection).
 *
 * Cascade: platform → tenant → user
 * - NULL at any level means "inherit all from level above"
 * - Explicit array means "only these modules (intersected with above)"
 */
export function getEffectiveEnabledModules(
  platformAvailableModules: string[] | null | undefined,
  tenantEnabledModules: string[] | null | undefined,
  userEnabledModules: string[] | null | undefined,
  moduleCeiling?: string[] | null | undefined
): string[] | null {
  // Start with platform level
  let effective: string[] | null = platformAvailableModules ?? null;

  // Intersect with tenant level
  if (tenantEnabledModules !== null && tenantEnabledModules !== undefined) {
    if (effective === null) {
      effective = tenantEnabledModules;
    } else {
      effective = effective.filter(m => tenantEnabledModules.includes(m));
    }
  }

  // Intersect with module ceiling (set by admin/inviter)
  if (moduleCeiling !== null && moduleCeiling !== undefined) {
    if (effective === null) {
      effective = moduleCeiling;
    } else {
      effective = effective.filter(m => moduleCeiling.includes(m));
    }
  }

  // Intersect with user level
  if (userEnabledModules !== null && userEnabledModules !== undefined) {
    if (effective === null) {
      effective = userEnabledModules;
    } else {
      effective = effective.filter(m => userEnabledModules.includes(m));
    }
  }

  return effective;
}

/**
 * Check if a specific toggleable module is enabled for the user.
 * Role-based modules (admin, sysadmin) are not checked here.
 */
export function isModuleEnabled(
  moduleId: string,
  effectiveEnabledModules: string[] | null
): boolean {
  // Always-available modules
  if (ALWAYS_AVAILABLE_MODULE_IDS.includes(moduleId)) {
    return true;
  }
  // Role-based modules are always "enabled" from settings perspective
  if (ROLE_BASED_MODULE_IDS.includes(moduleId)) {
    return true;
  }
  // If no restrictions, all toggleable modules are enabled
  if (effectiveEnabledModules === null) {
    return true;
  }
  // Check if module is in the enabled list
  return effectiveEnabledModules.includes(moduleId);
}

// Entity lookup utilities

/**
 * Get entity config by table name (searches all modules)
 */
export function getEntityByTable(tableName: string): { module: ModuleConfig; entity: EntityConfig; entityKey: string } | undefined {
  for (const module of ALL_MODULES) {
    for (const [entityKey, entity] of Object.entries(module.entities)) {
      if (entity.table === tableName) {
        return { module, entity, entityKey };
      }
    }
  }
  return undefined;
}

/**
 * Find an entity whose table name ends with `_${suffix}`.
 * Used when MCP API path yields a bare resource like "workspaces"
 * but the entity table is "tasks_workspaces".
 */
export function getEntityByTableSuffix(suffix: string): { module: ModuleConfig; entity: EntityConfig; entityKey: string } | undefined {
  const target = `_${suffix}`;
  for (const module of ALL_MODULES) {
    for (const [entityKey, entity] of Object.entries(module.entities)) {
      if (entity.table?.endsWith(target)) {
        return { module, entity, entityKey };
      }
    }
  }
  return undefined;
}

/**
 * Get all entities that are creatable (for quick-create menu)
 */
export function getCreatableEntities(): Array<{ module: ModuleConfig; entity: EntityConfig; entityKey: string }> {
  const result: Array<{ module: ModuleConfig; entity: EntityConfig; entityKey: string }> = [];
  for (const module of MODULES) {
    for (const [entityKey, entity] of Object.entries(module.entities)) {
      if (entity.creatable) {
        result.push({ module, entity, entityKey });
      }
    }
  }
  return result;
}

/**
 * Get creatable entities for a specific module (for module-specific quick-create menu)
 */
export function getCreatableEntitiesForModule(moduleId: string): Array<{ entity: EntityConfig; entityKey: string }> {
  const module = MODULES.find(m => m.id === moduleId);
  if (!module) return [];

  const result: Array<{ entity: EntityConfig; entityKey: string }> = [];
  for (const [entityKey, entity] of Object.entries(module.entities)) {
    if (entity.creatable) {
      result.push({ entity, entityKey });
    }
  }
  return result;
}

/**
 * Get all entity types with their display names (for audit log filters, etc.)
 */
export function getAllEntityTypes(): Array<{ table: string; displayNameKey: string; moduleId: string }> {
  const result: Array<{ table: string; displayNameKey: string; moduleId: string }> = [];
  for (const module of ALL_MODULES) {
    for (const entity of Object.values(module.entities)) {
      if (entity.table) {
        result.push({
          table: entity.table,
          displayNameKey: entity.displayNameKey,
          moduleId: module.id,
        });
      }
    }
  }
  return result;
}

/**
 * Get modules that have at least one entity with a table (for module filter pickers)
 */
export function getModulesWithEntities(): Array<{ id: string; nameKey: string }> {
  return ALL_MODULES
    .filter(m => Object.values(m.entities).some(e => e.table))
    .map(m => ({ id: m.id, nameKey: m.nameKey }));
}

// Must match VERSIONED_ENTITY_TYPES in shared/versioning/__init__.py
const VERSIONED_TABLES = new Set([
  'pipeline_deals',
  'contacts_contacts',
  'contacts_organizations',
  'pipeline_pipelines',
  'recruiting_candidates',
  'recruiting_job_postings',
  'recruiting_applications',
  'recruiting_pipelines',
  'people_employees',
  'accounting_journal_entries',
  'accounting_accounts',
  'accounting_fiscal_periods',
  'items',
  'boards',
  'products',
  'product_categories',
  'product_price_lists',
  'support_tickets',
  'support_kb_articles',
]);

export interface VersionedEntityType {
  table: string;
  displayNameKey: string;
  moduleId: string;
  icon: React.ReactNode | undefined;
}

export interface ModuleWithVersionedEntities {
  module: ModuleConfig;
  entities: VersionedEntityType[];
}

/**
 * Get modules that have versioned entities, with their entity list
 */
export function getModulesWithVersionedEntities(): ModuleWithVersionedEntities[] {
  const result: ModuleWithVersionedEntities[] = [];

  for (const module of ALL_MODULES) {
    const versionedEntities: VersionedEntityType[] = [];

    for (const entity of Object.values(module.entities)) {
      if (entity.table && VERSIONED_TABLES.has(entity.table)) {
        versionedEntities.push({
          table: entity.table,
          displayNameKey: entity.displayNameKey,
          moduleId: module.id,
          icon: entity.icon,
        });
      }
    }

    if (versionedEntities.length > 0) {
      result.push({ module, entities: versionedEntities });
    }
  }

  return result.sort((a, b) => a.module.displayOrder - b.module.displayOrder);
}

/**
 * Get versioned entity types with their display names and icons (for version history filters)
 */
export function getVersionedEntityTypes(): VersionedEntityType[] {
  const result: VersionedEntityType[] = [];

  for (const module of ALL_MODULES) {
    for (const entity of Object.values(module.entities)) {
      if (entity.table && VERSIONED_TABLES.has(entity.table)) {
        result.push({
          table: entity.table,
          displayNameKey: entity.displayNameKey,
          moduleId: module.id,
          icon: entity.icon,
        });
      }
    }
  }
  return result;
}
